import React from 'react';
import { Dialog, DialogProps as MuiDialogProps, useTheme } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';
import { Box } from '../Box';
import { Card } from '../Card';
import { Flex } from '../Flex';
import { Heading } from '../Heading';
import { IconButton } from '../IconButton';

export interface ModalProps extends Omit<
  MuiDialogProps,
  keyof PounceStyleProps | 'open' | 'title'
> {
  /** Whether the modal should be visible or not */
  isOpen: boolean;

  /** Callback fired when the component requests to be closed */
  onClose: () => void;

  /**
   * Whether the modal should be full screen
   * @default false
   */
  fullScreen?: boolean;

  /**  The title to add to the modal  */
  title?: string | React.ReactElement;

  /**
   * Whether to render a close button or not
   * @default false
   */
  showCloseButton?: boolean;

  /**
   * Additional action buttons to be displayed in header
   */
  actionButtons?: React.ReactElement;

  /** The id of the HTML node that contains the text of the modal */
  'aria-describedby'?: string;

  /** Callback fired when user hits "Escape" or clicks outside the dialog.
   *
   * When _strictly necessary_, this can be used to prevent a user from closing
   * the dialog, but otherwise it is important to allow users to close dialogs
   * for accessibility reasons.
   */
  onDismiss?: (event?: React.SyntheticEvent) => void;

  /**
   * The z-index to be applied to the modal overlay.
   * When not provided, uses theme.zIndex.modal (MUI default 1300) so the modal
   * stacks correctly above Drawer (1200) and other overlay components.
   */
  overlayZIndex?: number;

  /**
   * Applies padding to the modal body
   * @default true
   */
  padded?: boolean;

  /**
   * Applies overflow to the modal card
   * @default 'auto'
   */
  overflow?: 'auto' | 'hidden' | 'visible' | 'scroll';

  /**
   * Content to be rendered inside the modal
   */
  children?: React.ReactNode;

  /**
   * Optional styles to apply to the div container of the modal content
   */
  dialogContentStyles?: React.CSSProperties;
}

/**
 * A dialog variation that helps with grabbing the user's attention by blurring the background and
 * presenting an element that requires action from the user.
 *
 * Built on top of MUI Dialog component to provide the same API as the original pounce Modal
 * while leveraging MUI's accessibility and animation patterns.
 */
export const Modal = React.forwardRef<HTMLDivElement, ModalProps>(function Modal(
  {
    title,
    children,
    fullScreen = false,
    isOpen,
    onClose,
    onDismiss,
    showCloseButton = false,
    actionButtons,
    overlayZIndex,
    padded = true,
    overflow = 'auto',
    dialogContentStyles,
    'aria-describedby': ariaDescribedBy,
    ...rest
  },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);
  const theme = useTheme();
  const tokens = theme.palette?.tokens;
  const id = React.useId();
  const resolvedZIndex = overlayZIndex ?? theme.zIndex?.modal ?? 1300;

  const hasActionButtons = !!actionButtons || showCloseButton;

  const ModalContent = fullScreen ? Flex : Card;
  const borderColor = tokens?.stroke.neutral[5];
  const maxWidth = fullScreen ? '100%' : '90vw';
  const maxHeight = fullScreen ? '100%' : '90vh';
  const width = fullScreen ? '100%' : 'auto';
  const height = fullScreen ? '100%' : 'auto';
  const backgroundColor = tokens?.background.brand.dark.base;

  const modalContentProps = fullScreen ? { backgroundColor, flexDirection: 'column' } : {};

  return (
    <Dialog
      ref={ref}
      open={isOpen}
      onClose={onDismiss || onClose}
      fullScreen={fullScreen}
      aria-labelledby={id}
      aria-describedby={ariaDescribedBy}
      slotProps={{
        paper: {
          style: {
            ...dialogContentStyles,
          },
        },
      }}
      sx={{
        zIndex: resolvedZIndex,
        ...pounceStyles,
      }}
      {...otherProps}
    >
      <Flex justify="center" align="center" height="100%">
        <ModalContent
          minWidth="400px"
          maxWidth={maxWidth}
          maxHeight={maxHeight}
          width={width}
          height={height}
          position="relative"
          shadow="dark200"
          overflow={overflow}
          {...modalContentProps}
        >
          {title && (
            <Box
              as="header"
              p={6}
              sx={{
                position: 'relative',
                // Use ::after pseudo-element to create opacity
                // This keeps the heading text at full opacity while only the border line is transparent
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '1px',
                  backgroundColor: borderColor,
                  opacity: 1,
                },
              }}
            >
              <Heading variant="h5" fontWeight="bold" id={id}>
                {title}
              </Heading>
            </Box>
          )}
          {hasActionButtons && (
            <Flex align="center" gap={2} position="absolute" top={20} right={24} zIndex={1}>
              {actionButtons}
              {showCloseButton && (
                <IconButton
                  icon="close"
                  size="small"
                  aria-label="Dismiss Dialog"
                  variant="ghost"
                  onClick={onClose}
                  autoFocus
                />
              )}
            </Flex>
          )}
          {/* the overflow property here fixes a bug with full screen modals that have their own scrollable content */}
          <Box p={padded && 6} flex={1} overflow={overflow} data-testid="modal-content-body">
            {children}
          </Box>
        </ModalContent>
      </Flex>
    </Dialog>
  );
});
